import React from 'react';
import {
  Box, Button, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  Typography, InputLabel, FormControl, Chip, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { get, post, put, del } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

const ROLE_LABELS = ['Admin', 'Coordinator', 'Finance', 'Fundraising', 'User'];
const roleToLabel = (r) => {
  if (!r) return 'User';
  const s = String(r).trim();
  if (ROLE_LABELS.includes(s)) return s;
  const lower = s.toLowerCase();
  const match = ROLE_LABELS.find(l => l.toLowerCase() === lower);
  return match || 'User';
};

function groupPermissionsByArea(permissions) {
  const groups = {};
  (permissions || []).forEach(p => {
    const key = p.key || '';
    if (key === 'reports:view') return;
    let area = key.includes(':') ? key.split(':')[0] : 'other';
    if (key === 'event_reports:access' || key === 'sponsors_reports:access') {
      area = 'reports';
    }
    if (!groups[area]) groups[area] = [];
    groups[area].push(p);
  });
  const order = ['admin', 'calendar', 'orders', 'devotees', 'pledges', 'reports', 'communications', 'settings', 'users', 'meetings', 'other'];
  return order.filter(a => groups[a]?.length).map(area => ({ area, perms: groups[area] }));
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [allPermissions, setAllPermissions] = React.useState([]);
  const [rolesWithPermissions, setRolesWithPermissions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ name: '', email: '', role: 'User', status: 'active', permissions: [] });
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await get('/api/admin/users');
      setRows(d.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPermissionsAndRoles = React.useCallback(async () => {
    try {
      const [permsRes, rolesRes] = await Promise.all([
        get('/api/admin/permissions'),
        get('/api/admin/roles'),
      ]);
      setAllPermissions(permsRes.permissions || []);
      setRolesWithPermissions(rolesRes.roles || []);
    } catch {
      setAllPermissions([]);
      setRolesWithPermissions([]);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { loadPermissionsAndRoles(); }, [loadPermissionsAndRoles]);

  // When role dropdown changes, prefill permissions from that role's defaults
  const applyRoleDefaults = React.useCallback((roleName) => {
    const role = (rolesWithPermissions || []).find(r => r.name === roleName);
    const perms = Array.isArray(role?.permissions) ? [...role.permissions] : [];
    setForm(prev => ({ ...prev, permissions: perms }));
  }, [rolesWithPermissions]);

  const openAdd = () => {
    setEditing(null);
    const defaultRole = 'User';
    const role = (rolesWithPermissions || []).find(r => r.name === defaultRole);
    const defaultPerms = Array.isArray(role?.permissions) ? [...role.permissions] : [];
    setForm({ name: '', email: '', role: defaultRole, status: 'active', permissions: defaultPerms });
    setModalOpen(true);
    setOk('');
    setError('');
  };

  const openEdit = async (u) => {
    setEditing(u);
    setError('');
    setOk('');
    try {
      const userData = await get(`/api/admin/users/${u.id}`);
      const user = userData.user || userData;
      const role = roleToLabel(user.role);
      setForm({
        name: user.name || '',
        email: user.email || '',
        role: role,
        status: user.status || 'active',
        permissions: Array.isArray(user.permissions) ? [...user.permissions] : [],
      });
      setModalOpen(true);
    } catch (e) {
      setError(e?.message || 'Failed to load user');
    }
  };

  const close = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const togglePermission = (key, checked) => {
    setForm(prev => ({
      ...prev,
      permissions: checked
        ? [...(prev.permissions || []), key]
        : (prev.permissions || []).filter(k => k !== key),
    }));
  };

  const onSave = async () => {
    setError('');
    setOk('');
    try {
      // Keep admin:full off non-Admin users. Preserve every other permission already granted —
      // including hidden ones like reports:view — so editing a user never silently strips access.
      const perms = (form.permissions || []).filter(
        k => (k !== 'admin:full' || String(form.role || '').toLowerCase() === 'admin')
      );
      const payload = { name: form.name, email: form.email, role: form.role, status: form.status, permissions: perms };
      if (editing?.id) {
        await put(`/api/admin/users/${editing.id}`, payload);
        setOk('User updated');
      } else {
        await post('/api/admin/users', payload);
        setOk('User created');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    setOk('');
    try {
      await del(`/api/admin/users/${u.id}`);
      setOk('User deleted');
      load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'locked': return 'error';
      default: return 'default';
    }
  };

  const selectedPerms = new Set(form.permissions || []);
  // admin:full is only applicable for Admin role; hide for other roles
  const isAdminRole = String(form.role || '').toLowerCase() === 'admin';
  const permissionsForUser = (allPermissions || []).filter(
    p => (p.key !== 'admin:full' || isAdminRole) && p.key !== 'reports:view'
  );
  const grouped = groupPermissionsByArea(permissionsForUser);

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Users</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/manage/roles')}>Manage Roles</Button>
          <Button variant="contained" onClick={openAdd}>Add User</Button>
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      {error && <Box sx={{ mb: 2, color: 'error.main' }}>{error}</Box>}
      {ok && <Box sx={{ mb: 2, color: 'success.main' }}>{ok}</Box>}

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Login Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7}>No users</TableCell></TableRow>
            )}
            {rows.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>
                  <Chip
                    label={u.status || 'active'}
                    color={getStatusColor(u.status || 'active')}
                    size="small"
                  />
                </TableCell>
                <TableCell>{u.last_login ? new Date(u.last_login).toLocaleString() : ''}</TableCell>
                <TableCell>{u.login_count ?? 0}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openEdit(u)}>Edit</Button>
                    {String(u.role).toLowerCase() !== 'admin' && (
                      <Button size="small" color="error" variant="contained" onClick={() => onDelete(u)}>Delete</Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={modalOpen} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent dividers>
          {error && <Box sx={{ mb: 2, color: 'error.main', fontSize: '0.875rem' }}>{error}</Box>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} fullWidth disabled={!!editing} />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={form.role}
                onChange={e => {
                  const newRole = e.target.value;
                  setForm(prev => ({ ...prev, role: newRole }));
                  applyRoleDefaults(newRole);
                }}
              >
                {ROLE_LABELS.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Permissions below are pre-filled from this role&apos;s defaults. You can change them per user.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="locked">Locked</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Permissions</Typography>
            <FormGroup>
              {grouped.map(({ area, perms }) => (
                <Box key={area} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{area.replace(/_/g, ' ')}</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {perms.map(p => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            checked={selectedPerms.has(p.key)}
                            onChange={e => togglePermission(p.key, e.target.checked)}
                          />
                        }
                        label={p.key}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageLayout>
  );
}
